<template>
  <div class="operation-log-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>日志管理</span>
        </div>
      </template>

      <!-- 搜索表单 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="用户名">
          <el-input
            v-model="queryParams.username"
            placeholder="请输入用户名"
            clearable
            @keyup.enter="handleSearch"
            style="width: 180px"
          />
        </el-form-item>
        <el-form-item label="操作类型">
          <el-select v-model="queryParams.operationType" placeholder="全部" clearable style="width: 150px">
            <el-option
              v-for="item in operationTypeList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="用户类型">
          <el-select v-model="queryParams.userType" placeholder="全部" clearable style="width: 150px">
            <el-option
              v-for="item in userTypeList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="平台类型">
          <el-select v-model="queryParams.platformType" placeholder="全部" clearable style="width: 120px">
            <el-option
              v-for="item in platformTypeList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="操作状态">
          <el-select v-model="queryParams.status" placeholder="全部" clearable style="width: 120px">
            <el-option
              v-for="item in statusList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="时间范围">
          <el-date-picker
            v-model="dateRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
            style="width: 380px"
            @change="handleDateRangeChange"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 日志列表 -->
      <el-table
        v-loading="loading"
        :data="logList"
        border
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="createTime" label="操作时间" width="180">
          <template #default="{ row }">
            {{ formatDateTime(row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="username" label="操作用户" width="120" />
        <el-table-column prop="userType" label="用户类型" width="120">
          <template #default="{ row }">
            {{ getUserTypeName(row.userType) }}
          </template>
        </el-table-column>
        <el-table-column prop="operationType" label="操作类型" width="100">
          <template #default="{ row }">
            {{ getOperationTypeName(row.operationType) }}
          </template>
        </el-table-column>
        <el-table-column prop="module" label="操作模块" width="150" />
        <el-table-column prop="operationDesc" label="操作描述" width="180" show-overflow-tooltip />
        <el-table-column prop="requestUrl" label="请求URL" width="200" show-overflow-tooltip />
        <el-table-column prop="executionTime" label="执行耗时" width="100">
          <template #default="{ row }">
            {{ row.executionTime ? row.executionTime + 'ms' : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="操作状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="platformType" label="平台类型" width="100">
          <template #default="{ row }">
            {{ getPlatformTypeName(row.platformType) }}
          </template>
        </el-table-column>
        <el-table-column prop="ipAddress" label="IP地址" width="140" />
        <el-table-column label="操作" width="120" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleViewDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.current"
          v-model:page-size="queryParams.size"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 日志详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="日志详情"
      width="900px"
      @close="handleDetailDialogClose"
    >
      <el-descriptions :column="2" border v-if="logDetail">
        <el-descriptions-item label="日志ID">{{ logDetail.id }}</el-descriptions-item>
        <el-descriptions-item label="追踪ID">{{ logDetail.traceId }}</el-descriptions-item>
        <el-descriptions-item label="操作用户">{{ logDetail.username || '-' }}</el-descriptions-item>
        <el-descriptions-item label="用户类型">{{ getUserTypeName(logDetail.userType) }}</el-descriptions-item>
        <el-descriptions-item label="所属公司ID">{{ logDetail.companyId || '-' }}</el-descriptions-item>
        <el-descriptions-item label="操作类型">{{ getOperationTypeName(logDetail.operationType) }}</el-descriptions-item>
        <el-descriptions-item label="操作模块">{{ logDetail.module || '-' }}</el-descriptions-item>
        <el-descriptions-item label="操作描述">{{ logDetail.operationDesc || '-' }}</el-descriptions-item>
        <el-descriptions-item label="请求方法">{{ logDetail.requestMethod || '-' }}</el-descriptions-item>
        <el-descriptions-item label="请求URL" :span="2">
          <span style="word-break: break-all;">{{ logDetail.requestUrl || '-' }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="请求参数" :span="2">
          <el-input
            v-model="logDetail.requestParams"
            type="textarea"
            :rows="3"
            readonly
            style="width: 100%"
          />
        </el-descriptions-item>
        <el-descriptions-item label="请求体" :span="2">
          <el-input
            v-model="logDetail.requestBody"
            type="textarea"
            :rows="3"
            readonly
            style="width: 100%"
          />
        </el-descriptions-item>
        <el-descriptions-item label="响应状态码">{{ logDetail.responseCode || '-' }}</el-descriptions-item>
        <el-descriptions-item label="执行耗时">{{ logDetail.executionTime ? logDetail.executionTime + 'ms' : '-' }}</el-descriptions-item>
        <el-descriptions-item label="操作状态">
          <el-tag :type="logDetail.status === 1 ? 'success' : 'danger'">
            {{ logDetail.status === 1 ? '成功' : '失败' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="平台类型">{{ getPlatformTypeName(logDetail.platformType) }}</el-descriptions-item>
        <el-descriptions-item label="IP地址">{{ logDetail.ipAddress || '-' }}</el-descriptions-item>
        <el-descriptions-item label="User-Agent" :span="2">
          <span style="word-break: break-all;">{{ logDetail.userAgent || '-' }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="响应体" :span="2" v-if="logDetail.responseBody">
          <el-input
            v-model="logDetail.responseBody"
            type="textarea"
            :rows="5"
            readonly
            style="width: 100%"
          />
        </el-descriptions-item>
        <el-descriptions-item label="错误信息" :span="2" v-if="logDetail.errorMessage">
          <el-alert
            :title="logDetail.errorMessage"
            type="error"
            :closable="false"
            show-icon
          />
        </el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDateTime(logDetail.createTime) }}</el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="handleViewTrace" v-if="logDetail && logDetail.traceId">
          查看追踪日志
        </el-button>
      </template>
    </el-dialog>

    <!-- 追踪日志对话框 -->
    <el-dialog
      v-model="traceDialogVisible"
      title="追踪日志"
      width="1200px"
    >
      <el-table
        :data="traceLogList"
        border
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="createTime" label="操作时间" width="180">
          <template #default="{ row }">
            {{ formatDateTime(row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="operationType" label="操作类型" width="100">
          <template #default="{ row }">
            {{ getOperationTypeName(row.operationType) }}
          </template>
        </el-table-column>
        <el-table-column prop="module" label="操作模块" width="150" />
        <el-table-column prop="operationDesc" label="操作描述" width="180" />
        <el-table-column prop="requestUrl" label="请求URL" width="200" show-overflow-tooltip />
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleViewDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <template #footer>
        <el-button @click="traceDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import {
  getOperationLogPage,
  getOperationLogById,
  getOperationLogsByTraceId,
  getOperationTypes,
  getPlatformTypes,
  getUserTypes,
  getStatusList
} from '../api/operationLog'

// 数据
const loading = ref(false)
const logList = ref([])
const total = ref(0)
const dateRange = ref(null)
const queryParams = reactive({
  username: '',
  operationType: '',
  userType: '',
  platformType: '',
  status: null,
  startTime: null,
  endTime: null,
  current: 1,
  size: 20
})

// 下拉选项列表
const operationTypeList = ref([])
const platformTypeList = ref([])
const userTypeList = ref([])
const statusList = ref([])

// 详情对话框
const detailDialogVisible = ref(false)
const logDetail = ref(null)

// 追踪日志对话框
const traceDialogVisible = ref(false)
const traceLogList = ref([])

// 方法
const formatDateTime = (dateTime) => {
  if (!dateTime) return '-'
  const date = new Date(dateTime)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const getUserTypeName = (userType) => {
  const typeMap = {
    'SALESMAN': '业务员',
    'MANAGER': '管理员',
    'ACCOUNTANT': '会计',
    'SYSTEM_ADMIN': '系统管理员',
    'ASSISTANT': '内勤'
  }
  return typeMap[userType] || userType || '-'
}

const getOperationTypeName = (operationType) => {
  const typeMap = {
    'CREATE': '新增',
    'UPDATE': '更新',
    'DELETE': '删除',
    'QUERY': '查询',
    'LOGIN': '登录',
    'LOGOUT': '登出',
    'EXPORT': '导出',
    'IMPORT': '导入'
  }
  return typeMap[operationType] || operationType || '-'
}

const getPlatformTypeName = (platformType) => {
  const typeMap = {
    'PC': 'PC端',
    'MINI': '小程序端'
  }
  return typeMap[platformType] || platformType || '-'
}

const handleDateRangeChange = (dates) => {
  if (dates && dates.length === 2) {
    queryParams.startTime = dates[0]
    queryParams.endTime = dates[1]
  } else {
    queryParams.startTime = null
    queryParams.endTime = null
  }
}

const loadLogList = async () => {
  loading.value = true
  try {
    // 构建查询参数，过滤空值
    const params = {}
    if (queryParams.username) params.username = queryParams.username
    if (queryParams.operationType) params.operationType = queryParams.operationType
    if (queryParams.userType) params.userType = queryParams.userType
    if (queryParams.platformType) params.platformType = queryParams.platformType
    if (queryParams.status !== null && queryParams.status !== '') params.status = queryParams.status
    if (queryParams.startTime) params.startTime = queryParams.startTime
    if (queryParams.endTime) params.endTime = queryParams.endTime
    params.current = queryParams.current
    params.size = queryParams.size

    const res = await getOperationLogPage(params)
    if (res.code === '000000') {
      logList.value = res.data.records || []
      total.value = res.data.total || 0
    }
  } catch (error) {
    console.error('获取日志列表失败:', error)
    ElMessage.error('获取日志列表失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  queryParams.current = 1
  loadLogList()
}

const handleReset = () => {
  queryParams.username = ''
  queryParams.operationType = ''
  queryParams.userType = ''
  queryParams.platformType = ''
  queryParams.status = null
  queryParams.startTime = null
  queryParams.endTime = null
  dateRange.value = null
  queryParams.current = 1
  loadLogList()
}

const handleViewDetail = async (row) => {
  try {
    const res = await getOperationLogById(row.id)
    if (res.code === '000000') {
      logDetail.value = res.data
      detailDialogVisible.value = true
    }
  } catch (error) {
    console.error('获取日志详情失败:', error)
    ElMessage.error('获取日志详情失败')
  }
}

const handleDetailDialogClose = () => {
  logDetail.value = null
}

const handleViewTrace = async () => {
  if (!logDetail.value || !logDetail.value.traceId) {
    ElMessage.warning('追踪ID不存在')
    return
  }
  try {
    const res = await getOperationLogsByTraceId(logDetail.value.traceId)
    if (res.code === '000000') {
      traceLogList.value = res.data || []
      traceDialogVisible.value = true
    }
  } catch (error) {
    console.error('获取追踪日志失败:', error)
    ElMessage.error('获取追踪日志失败')
  }
}

const handleSizeChange = (size) => {
  queryParams.size = size
  loadLogList()
}

const handlePageChange = (page) => {
  queryParams.current = page
  loadLogList()
}

// 加载下拉选项
const loadOptions = async () => {
  try {
    // 并行加载所有选项
    const [operationTypesRes, platformTypesRes, userTypesRes, statusListRes] = await Promise.all([
      getOperationTypes(),
      getPlatformTypes(),
      getUserTypes(),
      getStatusList()
    ])

    if (operationTypesRes.code === '000000') {
      operationTypeList.value = operationTypesRes.data || []
    }
    if (platformTypesRes.code === '000000') {
      platformTypeList.value = platformTypesRes.data || []
    }
    if (userTypesRes.code === '000000') {
      userTypeList.value = userTypesRes.data || []
    }
    if (statusListRes.code === '000000') {
      statusList.value = statusListRes.data || []
    }
  } catch (error) {
    console.error('加载选项失败:', error)
  }
}

// 生命周期
onMounted(() => {
  loadLogList()
  loadOptions()
})
</script>

<style scoped>
.operation-log-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>

